import {
  CUSTOM_ERROR_HANDLERS_CREATOR,
  CustomErrorHandlersCreator,
  REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE
} from '@aviary/interceptors/http-error-interceptor.types';
import {HttpContext} from '@angular/common/http';
import {RouterActions, SnackBarActions} from '@store/aviary/aviary.adapter';
import {RegistrationActions} from './registration.actions';

export function contextWithErrorHandlerForRequestVerificationEmail(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // email already exists on patron other than current authorized patron
          errorCodes: 'GENERAL_DUPLICATE_EMAIL',
          actions: SnackBarActions.showError('This email is already in use, please choose another.')
        },
        {
          // email is blank or already verified
          errorCodes: 'GENERAL_INVALID_VALUE',
          actions: SnackBarActions.showError(REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE)
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForVerifyEmail(doNavigates: boolean): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          errorCodes: 'PATRON_VERIFICATION_EXPIRED',
          actions: [
            SnackBarActions.showError('Verification code has expired.'),
            RegistrationActions.setVerificationExpired(true),
            ...doNavigates ? [RouterActions.navigateTo('INDEX')] : []
          ]
        },
        {
          errorCodes: 'PATRON_VERIFICATION_CODE_INCORRECT',
          actions: [
            SnackBarActions.showError('Verification code is incorrect.'),
            ...doNavigates ? [RouterActions.navigateTo('INDEX')] : []
          ]
        },
        {
          // ENTITY_ID_NOT_FOUND: failed to find the verification object from the uuid,
          // PATRON_VERIFICATION_INVALID_TYPE: verification is not an email verification, may be a phone call or sms verification
          // PATRON_VERIFICATION_INVALID_STATUS: verification status is invalid, possibly from finished registration or from failing too many attempts
          errorCodes: ['ENTITY_ID_NOT_FOUND', 'PATRON_VERIFICATION_INVALID_TYPE', 'PATRON_VERIFICATION_INVALID_STATUS'],
          actions: [
            SnackBarActions.showError('Failed to verify the email. Please refresh, resend the code, and try again. If the problem persists, contact your library staff.'),
            ...doNavigates ? [RouterActions.navigateTo('INDEX')] : []
          ]
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForRegister(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // Registration was previously completed with this verification code
          errorCodes: 'PATRON_VERIFICATION_ALREADY_USED',
          actions: [
            SnackBarActions.showError('Looks like you\'ve already completed registration. Please log in.'),
            RouterActions.navigateTo('INDEX')
          ]
        },
        {
          errorCodes: [
            'PATRON_VERIFICATION_NOT_FOUND', // Patron verification was not found for the submitted code
            'PATRON_VERIFICATION_INVALID_TYPE', // Patron verification type is invalid for this action
            'PATRON_VERIFICATION_NOT_APPROVED', // Patron verification not in approved status
            'GENERAL_INVALID_VALUE', // Patron verification has a patron associated already
            'PATRON_NOTIFICATION_METHOD_CONFLICT', // Multiple notification methods selected
            'PATRON_NOTIFICATION_METHOD_REQUIRED', // Missing notification method
            'PATRON_SMS_PHONE_REQUIRED', // Missing mobile phone number while text notifications is selected
            'PATRON_SMS_TERMS_REQUIRED', // SMS terms must be accepted while text notifications is selected
            'GENERAL_DUPLICATE_EMAIL' // Email is already in use
          ],
          actions: SnackBarActions.showError('There was an error during registration. ' + REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE)
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForRegisterExisting(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // Registration was previously completed with this verification code
          errorCodes: ['PATRON_VERIFICATION_ALREADY_USED', 'PATRON_ALREADY_REGISTERED'],
          actions: [
            SnackBarActions.showError('Looks like you\'ve already completed registration. Please log in.'),
            RouterActions.navigateTo('INDEX')
          ]
        },
        {
          errorCodes: [
            'PATRON_VERIFICATION_NOT_FOUND',  // Patron verification was not found for the submitted code
            'PATRON_VERIFICATION_WRONG_REGISTRATION_TYPE', // Patron verification is for a different registration type
            'PATRON_VERIFICATION_NOT_APPROVED', // Patron verification not in approved status
            'ENTITY_ID_NOT_FOUND', // Patron not found
            'GENERAL_DUPLICATE_EMAIL' // Email is already in use
          ],
          actions: SnackBarActions.showError('There was an error during registration. ' + REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE)
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}
